import React, { lazy } from 'react';
import { hot } from 'react-hot-loader/root';
import { lazily } from 'react-lazily';
import { observer, inject } from 'mobx-react';
import { Switch, Route, Redirect } from 'react-router-dom';

import { AuthSwitch } from 'common/routes';
import { identify } from 'utils/analytics';
import { getCookie } from 'utils/helpers/cookie';
import { setCookie } from 'utils/helpers/cookie';
import { isProdEnv } from 'common/helpers';

import { SVGIcon, Video, ResponsiveImage } from 'common/components';

const {
  DesktopHeader,
  MobileNav,
  Footer,
  BookTickets,
  Notifications,
  ConsentBanner,
  NavigationHeader,
  NavigationFooter,
  MetaData,
  FloatingWidget,
} = lazily(() => import('public/components'));

// import {
//   AngryBirds,
//   AngryBirdsTicketInfo,
//   AngryBirdsFaq,
//   ArticleList,
//   Article,
//   Community,
//   ContentPage,
//   Directory,
//   Entertainment,
//   Food,
//   HomeNew,
//   Login,
//   Logout,
//   NickUniverseHome,
//   NickUniverseTicketInfo,
//   NickUniverseFaq,
//   NotFound,
//   ProfileTickets,
//   ProfileOrders,
//   ProfilePreferencesEmail,
//   ProfilePreferencesPrivacy,
//   RinkHome,
//   RinkTicketInfo,
//   RinkFaq,
//   BigSnowHome,
//   BigSnowTicketInfo,
//   BigSnowFaq,
//   DreamWorksHome,
//   DreamWorksTicketInfo,
//   DreamWorksFaq,
//   DreamWorksMap,
//   BlacklightHome,
//   BlacklightTicketInfo,
//   BlacklightFaq,
//   MirrorMazeHome,
//   MirrorMazeTicketInfo,
//   MirrorMazeFaq,
//   Services,
//   Shop,
//   Signup,
//   SignupReferrer,
//   RequestPassword,
//   SetPassword,
//   VenueDetails,
//   Ride,
//   VenueTicketInfo,
//   VenueFaq,
//   Visit,
//   WifiHome,
//   HoursAndDirections,
//   Contact,
//   Careers,
//   NewsletterSubscribe,
//   DreamSafeHome,
//   RollerCheckout,
//   BundleTickets,
//   BundleAddons,
//   OrderUpdate,
//   OrderUpdateCheckout,
//   OrderUpdateSuccess,
//   OrderUpdateAddons,
//   OrderUpdateBundleAddons,
//   Deals,
//   Chat,
//   DealDetail,
//   Sitemap,
//   AdEvents,
//   AdEventDetail,
//   MiniGolfScoreHome,
//   MiniGolfScorePlayers,
//   MiniGolfScoreRules,
//   MiniGolfScoreBoard,
//   MiniGolfScoreWinner,
// } from 'public/screens';
const {
  AngryBirdsTicketInfo,
  AngryBirdsFaq,
  ArticleList,
  Article,
  Community,
  ContentPage,
  Directory,
  Entertainment,
  Food,
  HomeNew,
  Login,
  Logout,
  NickUniverseTicketInfo,
  NickUniverseFaq,
  NotFound,
  ProfileOrders,
  ProfileHub,
  ProfileSettings,
  ProfileInfo,
  ProfileCommunicationPreferences,
  ProfilePrivacySettings,
  ProfilePrivacyConsent,
  ProfileDeleteAccount,
  ProfilePreferencesPrivacy,
  ProfileFavorites,
  ProfileFavoriteCharacters,
  ProfileBookings,
  RinkTicketInfo,
  RinkFaq,
  BigSnowTicketInfo,
  BigSnowFaq,
  DreamWorksTicketInfo,
  DreamWorksFaq,
  BlacklightHome,
  BlacklightTicketInfo,
  BlacklightFaq,
  MirrorMazeHome,
  MirrorMazeTicketInfo,
  MirrorMazeFaq,
  Services,
  Shop,
  Signup,
  SignupReferrer,
  RequestPassword,
  SetPassword,
  VenueDetails,
  VenueEventDetails,
  VenueMap,
  Ride,
  VenueTicketInfo,
  VenueFaq,
  Visit,
  WifiHome,
  HoursAndDirections,
  Help,
  Careers,
  NewsletterSubscribe,
  DreamSafeHome,
  RollerCheckout,
  BundleTickets,
  BundleAddons,
  OrderUpdate,
  OrderUpdateCheckout,
  OrderUpdateSuccess,
  OrderUpdateAddons,
  OrderUpdateBundleAddons,
  Deals,
  Chat,
  GroupSalesChat,
  DealDetail,
  Sitemap,
  AdEvents,
  AdEventDetail,
  MiniGolfScoreHome,
  MiniGolfScorePlayers,
  MiniGolfScoreRules,
  MiniGolfScoreBoard,
  MiniGolfScoreWinner,
  DreamAppResetPin,
  DreamAppResetPassword,
  PayForParkingTickets,
  PayForParkingPayment,
  PayForParkingCongrats,
  FlipBook,
  TenantJobs,
} = lazily(() => import('public/screens'));

// const TicketsScreenContainer = lazy(() =>
//   import('public/screens/NewCheckout/Tickets')
// );
const TicketsScreenContainer = lazy(() =>
  import('public/screens/NewCheckout/Tickets')
);
const AddonsScreenContainer = lazy(() =>
  import('public/screens/NewCheckout/Addons')
);
const SeasonProfilesScreenContainter = lazy(() =>
  import('public/screens/NewCheckout/SeasonProfiles')
);
const BasketScreenContainer = lazy(() =>
  import('public/screens/NewCheckout/Basket')
);
const PaymentScreenContainer = lazy(() =>
  import('public/screens/NewCheckout/Payment')
);
const CongratulationsScreenContainer = lazy(() =>
  import('public/screens/NewCheckout/Congratulations')
);
const BirthdayTickets = lazy(() =>
  import('public/screens/NewCheckout/Birthday/Birthday')
);
const BirthdayFormSubmitted = lazy(() =>
  import(
    'public/screens/NewCheckout/Birthday/BirthdayFormSubmitted/BirthdayFormSubmitted'
  )
);
const BirthdayEntertainment = lazy(() =>
  import(
    'public/screens/NewCheckout/Birthday/Entertainment/BirthdayEntertainment'
  )
);
const BirthdayMerchandise = lazy(() =>
  import('public/screens/NewCheckout/Birthday/Merchandise/BirthdayMerchandise')
);
const BirthdayFoodAndBeverage = lazy(() =>
  import(
    'public/screens/NewCheckout/Birthday/BirthdayFoodAndBeverage/BirthdayFoodAndBeverage'
  )
);
const BirthdayReview = lazy(() =>
  import('public/screens/NewCheckout/Birthday/Review/Review')
);
const BirthdayInfo = lazy(() =>
  import('public/screens/NewCheckout/Birthday/Info/Info')
);
const BirthdayPayment = lazy(() =>
  import('public/screens/NewCheckout/Birthday/Payment')
);
const BirthdayPaymentConfirmation = lazy(() =>
  import(
    'public/screens/NewCheckout/Birthday/Payment/Confirmation/Confirmation'
  )
);

import Preview from '../../screens/Preview';
import BrazeIframe from '../../components/BrazeIframe';
import { ChatIcon } from '../../components/ChatIcon';
import { GladlyWidget } from '../../components/GladlyWidget';

// Icons
import ICON_URL from 'public/assets/icons.svg';
SVGIcon.setBaseUrl(ICON_URL);

// Static Images
import CONTENTFUL_IMAGES from 'public/assets/contentful-images.json';
import {
  BUNDLES_HUB_PATH,
  BUNDLE_PAGE_PATH,
  LEGACY_BUNDLE_PATH,
  getBundleRedirect,
} from './bundleRoutes';
ResponsiveImage.useContentfulImages(CONTENTFUL_IMAGES);
Video.useContentfulImages(CONTENTFUL_IMAGES);

// Excluded routes for Buy Tickets button
// It seems like the last regex was matching everything besides the homepage
// Now the homepage has a different book tickets button,
// So I'm excluding every route, and maybe we can even remove this logic (if the buy button should be just on the hp)
const excludedRoutes = [/.*/];

import './default-layout.less';

@inject('menu', 'announcements', 'appSession', 'me', 'betaFeatures')
@observer
class DefaultLayout extends React.Component {
  componentDidUpdate() {
    window.scrollTo(0, 0);
  }

  assignUTMParams = () => {
    try {
      const utmParams = {};
      const searchParams = new URLSearchParams(document.location.search);
      utmParams.utm_source = searchParams.get('utm_source');
      utmParams.utm_medium = searchParams.get('utm_medium');
      utmParams.utm_campaign = searchParams.get('utm_campaign');
      utmParams.utm_id = searchParams.get('utm_id');
      utmParams.utm_term = searchParams.get('utm_term');
      utmParams.utm_content = searchParams.get('utm_content');
      if (Object.values(utmParams).every((param) => !param)) {
        if (document.referrer) {
          utmParams.utm_source = document.referrer;
          utmParams.utm_medium = 'Referral';
        } else {
          utmParams.utm_source = 'Direct';
        }
      }
      return setCookie('utmParams', JSON.stringify(utmParams), 60);
    } catch (e) {
      console.error(`Error assigning UTM params, Error:${e.message}`);
    }
  };

  async componentDidMount() {
    await this.props.announcements.search({
      type: [
        'nav-play',
        'nav-explore',
        'nav-shop',
        'footer',
        'homepage-spotlight',
        'banner',
      ],
    });
    await this.fetchUser();
    await this.props.menu.fetch();
    const isNavigationRedesignEnabled = await this.props.betaFeatures.fetch(
      'Navigation Redesign'
    );
    this.assignUTMParams();
    this.setState({ isNavigationRedesignEnabled });
  }

  redirectNonProd = () => {
    if (isProdEnv()) return;

    if (getCookie('is_dev') === '1') return;
    if (location?.pathname?.split('/')?.[1] === 'preview') return;

    const { user } = this.props.me;
    if (!user?.roles.includes('admin'))
      window.location.href = 'https://americandream.com';
  };

  async fetchUser() {
    if (this.props.appSession.isLoggedIn()) {
      const user = await this.props.me.fetch();
      identify(user);
    } else {
      // The anonymous identify call appears to have an
      // effect on the FB pixel so need to keep it here.
      identify();
    }
  }

  render() {
    return (
      <React.Fragment>
        <MetaData pathname={this.props.location.pathname} />
        {this.state?.isNavigationRedesignEnabled !== undefined &&
          (this.state?.isNavigationRedesignEnabled ? (
            <NavigationHeader />
          ) : (
            this.props.menu.menu && (
              <>
                <DesktopHeader />
                <MobileNav />
              </>
            )
          ))}
        <main>
          <Notifications />
          <Switch>
            <Route path="/" component={HomeNew} exact />
            <Route path="/sitemap" component={Sitemap} exact />
            <Route path="/shop" component={Shop} exact />
            <Route path="/visit" component={Visit} exact />
            <Route path="/entertainment" component={Entertainment} />
            <Route path="/food-and-drink" component={Food} />
            <Route path="/services" component={Services} />
            <Route path="/cart/checkout" component={PaymentScreenContainer} />
            <Route
              path="/cart/completed"
              component={CongratulationsScreenContainer}
            />
            <Route path="/tenant-employment" component={TenantJobs} exact />
            <Route path="/cart" component={BasketScreenContainer} exact />
            <Route path="/articles/:category" component={ArticleList} />
            <Route path="/article/:slug" component={Article} />
            <Route path="/directory/:type?/:flag?" component={Directory} />
            <Route path="/flipbook/:slug" component={FlipBook} />
            <Route
              path="/pay-for-parking/payment"
              component={PayForParkingPayment}
            />
            <Route
              path="/pay-for-parking/completed"
              component={PayForParkingCongrats}
            />
            <Route
              path="/pay-for-parking/tickets/:ticketId"
              component={PayForParkingTickets}
            />
            <Route
              path="/pay-for-parking/tickets"
              component={PayForParkingTickets}
            />
            <Route
              exact
              path="/events/category/:category?"
              component={AdEvents}
            />
            <Route path="/events/:slug" component={AdEventDetail} />
            <Route path="/community" component={Community} />
            <Route
              exact
              path="/deals/category/:currentCategory?"
              component={Deals}
            />
            <Route path="/deals/:slug" component={DealDetail} />
            <Route
              path="/venue/nickelodeon-universe/ticket-info"
              component={NickUniverseTicketInfo}
              exact
            />
            <Route
              path="/venue/nickelodeon-universe/faq"
              component={NickUniverseFaq}
              exact
            />

            {/*
              `/venue/the-rink` is now served from Contentful's Custom Landing Page via the generic 
              route `/venue/:slug` (VenueDetails). The hardcoded RinkHome screen was intentionally
              disabled. The ticket-info/faq sub-routes are retained in case the landing page links to them.
            */}
            <Route
              path="/venue/the-rink/ticket-info"
              component={RinkTicketInfo}
              exact
            />
            <Route path="/venue/the-rink/faq" component={RinkFaq} exact />

            <Route
              path="/venue/big-snow/ticket-info"
              component={BigSnowTicketInfo}
              exact
            />
            <Route path="/venue/big-snow/faq" component={BigSnowFaq} exact />
            <Route
              path="/venue/dreamworks-water-park/ticket-info"
              component={DreamWorksTicketInfo}
              exact
            />
            <Route
              path="/venue/dreamworks-water-park/faq"
              component={DreamWorksFaq}
              exact
            />
            <Route
              path="/venue/blacklight-mini-golf"
              component={BlacklightHome}
              exact
            />
            <Route
              path="/venue/blacklight-mini-golf/ticket-info"
              component={BlacklightTicketInfo}
              exact
            />
            <Route
              path="/dream-app/reset-pin/:token"
              component={DreamAppResetPin}
              exact
            />
            <Route
              path="/dream-app/reset-password/:token"
              component={DreamAppResetPassword}
              exact
            />
            <Route
              path="/venue/blacklight-mini-golf/faq"
              component={BlacklightFaq}
              exact
            />
            <Route path="/venue/mirrormaze" component={MirrorMazeHome} exact />
            <Route
              path="/venue/mirrormaze/ticket-info"
              component={MirrorMazeTicketInfo}
              exact
            />
            <Route
              path="/venue/mirrormaze/faq"
              component={MirrorMazeFaq}
              exact
            />
            <Route
              path="/venue/angry-birds-mini-golf/ticket-info"
              component={AngryBirdsTicketInfo}
              exact
            />
            <Route
              path="/venue/angry-birds-mini-golf/faq"
              component={AngryBirdsFaq}
              exact
            />

            <Route
              path="/venue/nickelodeon-universe-test"
              render={() => <Redirect to="/venue/nickelodeon-universe" />}
              exact
            />

            <Route
              path="/venue/dreamworks-water-park-test"
              render={() => <Redirect to="/venue/dreamworks-water-park" />}
              exact
            />

            <Route path="/venue/:slug" component={VenueDetails} exact />

            <Route
              path="/venue/:slug/attraction/:attractionSlug"
              component={VenueEventDetails}
              exact
            />

            <Route path="/venue/:slug/map" component={VenueMap} exact />

            <Route path="/bundle/:slug/addons" component={BundleAddons} />

            <Route path="/bundle/:slug" component={BundleTickets} />

            <Route path={BUNDLE_PAGE_PATH} component={ContentPage} exact />

            <Route path={`${BUNDLES_HUB_PATH}/:slug`} component={NotFound} />

            <Route
              path={LEGACY_BUNDLE_PATH}
              exact
              render={({ location }) => (
                <Redirect to={getBundleRedirect(location)} />
              )}
            />

            <Route
              path="/birthday/:slug/tickets/:date?"
              component={BirthdayTickets}
            />

            <Route
              path="/birthday/:slug/form-submitted"
              component={BirthdayFormSubmitted}
            />

            <Route
              path="/birthday/:slug/addons"
              component={BirthdayEntertainment}
            />

            <Route
              path="/birthday/:slug/merchandise"
              component={BirthdayMerchandise}
            />

            <Route
              path="/birthday/:slug/food-and-beverage"
              component={BirthdayFoodAndBeverage}
            />

            <Route path="/birthday/:slug/review" component={BirthdayReview} />

            <Route path="/birthday/:slug/info" component={BirthdayInfo} />

            <Route path="/birthday/:slug/payment" component={BirthdayPayment} />

            <Route path="/preview/:env/:entryId" component={Preview} />

            <Route
              path="/birthday/:slug/payment-confirmed"
              component={BirthdayPaymentConfirmation}
            />

            <Route
              path="*/:slug/tickets/:date?"
              component={TicketsScreenContainer}
            />

            <Route path="*/:slug/addons" component={AddonsScreenContainer} />

            <Route
              path="*/:slug/season-profiles"
              component={SeasonProfilesScreenContainter}
            />

            <Route
              path="/venue/:slug/ticket-info"
              component={VenueTicketInfo}
            />

            <Route path="/venue/:slug/faq" component={VenueFaq} />

            <Route path="/venue/:slug/attraction/:rideSlug" component={Ride} />

            <Route
              path="/promo/:slug/addons"
              component={AddonsScreenContainer}
            />

            <Route
              path="/promo/:slug/:date?"
              component={TicketsScreenContainer}
            />

            <Route path="/dreamsafe" component={DreamSafeHome} />

            <AuthSwitch
              path="/order-update"
              loggedIn={OrderUpdate}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/order-update-addons"
              loggedIn={OrderUpdateAddons}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/order-update-bundle-addons"
              loggedIn={OrderUpdateBundleAddons}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/order-update-checkout"
              loggedIn={OrderUpdateCheckout}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/order-update-success"
              loggedIn={OrderUpdateSuccess}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/orders"
              loggedIn={ProfileBookings}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/orders-old"
              loggedIn={ProfileOrders}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/hub"
              loggedIn={ProfileHub}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/settings"
              loggedIn={ProfileSettings}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/info"
              loggedIn={ProfileInfo}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/favorites"
              loggedIn={ProfileFavorites}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/favorites/characters"
              loggedIn={ProfileFavoriteCharacters}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/bookings"
              loggedIn={ProfileBookings}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/preferences/communications"
              loggedIn={ProfileCommunicationPreferences}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/preferences/privacy-settings"
              loggedIn={ProfilePrivacySettings}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />
            <AuthSwitch
              path="/profile/preferences/privacy-consent"
              loggedIn={ProfilePrivacyConsent}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />
            <AuthSwitch
              path="/profile/preferences/delete-account"
              loggedIn={ProfileDeleteAccount}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />

            <AuthSwitch
              path="/profile/preferences/email"
              loggedIn={ProfileCommunicationPreferences}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />
            <AuthSwitch
              path="/profile/preferences/privacy"
              loggedIn={ProfilePrivacyConsent}
              loggedOut={() => <Redirect to="/signup" />}
              exact
            />
            <Route
              path="/manage-cookies"
              component={(props) => (
                <ProfilePreferencesPrivacy hideTabs {...props} />
              )}
            />
            <AuthSwitch
              path="/profile"
              loggedIn={() => <Redirect to="/profile/hub" />}
              loggedOut={() => <Redirect to="/login" />}
              exact
            />
            <AuthSwitch
              exact
              path="/signup"
              loggedOut={Signup}
              loggedIn={() => <Redirect to="/profile" />}
            />
            <Route
              exact
              path="/signup/referrer/:code"
              component={SignupReferrer}
            />
            <AuthSwitch
              exact
              path="/login"
              loggedOut={Login}
              loggedIn={() => <Redirect to="/profile" />}
            />
            <AuthSwitch
              exact
              path="/forgot-password"
              loggedOut={RequestPassword}
              loggedIn={() => <Redirect to="/profile" />}
            />
            <AuthSwitch
              exact
              path="/reset-password"
              loggedOut={SetPassword}
              loggedIn={() => <Redirect to="/profile" />}
            />
            <Route
              exact
              path="/signup/referrer/:code"
              component={SignupReferrer}
            />

            <Route exact path="/logout" component={Logout} />
            <Route exact path="/wifi/welcome" component={WifiHome} />
            <Route
              path="/hours-and-directions"
              component={HoursAndDirections}
            />
            <Route path="/help" component={Help} />

            <Route path="/chat" component={Chat} />

            <Route path="/group-sales-test-chat" component={GroupSalesChat} />

            <Route path="/tenant-careers" component={Careers} />

            <Route
              path="/venue/:slug/score-card/players"
              component={MiniGolfScorePlayers}
            />

            <Route
              path="/venue/:slug/score-card/rules"
              component={MiniGolfScoreRules}
            />

            <Route
              path="/venue/:slug/score-card/board"
              component={MiniGolfScoreBoard}
            />

            <Route
              path="/venue/:slug/score-card/winner"
              component={MiniGolfScoreWinner}
            />

            <Route
              path="/venue/:slug/score-card"
              component={MiniGolfScoreHome}
            />

            <Redirect path="/news" to="/newsletter" exact />
            <Redirect
              path="/newsletter/subscribe/notify"
              to="/newsletter"
              exact
            />

            <Redirect
              path="/newsletter/subscribe/waterpark"
              to="/newsletter/subscribe/dreamworks"
              exact
            />
            <Redirect path="/newsletter/subscribe/" to="/newsletter" exact />

            <AuthSwitch
              path="/newsletter/subscribe/:group"
              loggedIn={() => <Redirect to="/profile/preferences/email" />}
              loggedOut={NewsletterSubscribe}
              exact
            />

            <Redirect
              path="/venue/:slug/pricing"
              to="/venue/:slug/tickets"
              exact
            />

            <Redirect path="/thrills-and-chills" to="/" exact />
            <Redirect path="/make-a-splash" to="/" exact />
            <Redirect path="/every-day-is-a-snow-day" to="/" exact />

            <Route
              path="/tickets/:checkoutslug"
              component={RollerCheckout}
              exact
            />

            <Route
              path="/redeem/:checkoutslug"
              component={RollerCheckout}
              exact
            />

            <Route path="/buy/:checkoutslug" component={RollerCheckout} exact />

            <Redirect from="/about" to="/our-story" />
            <Route path="/:slug" component={ContentPage} />
            <Route component={NotFound} />
          </Switch>
        </main>
        {this.state?.isNavigationRedesignEnabled ? (
          <NavigationFooter />
        ) : (
          <Footer />
        )}
        <BookTickets excludedRoutes={excludedRoutes} floatingComponent={true} />
        <BrazeIframe />
        <ConsentBanner />
        <ChatIcon />
        <GladlyWidget />
        <FloatingWidget />
      </React.Fragment>
    );
  }
}

export default hot(DefaultLayout);
